/**
 * useNorthstarSizing — thin wrapper over `layoutTokens.js`.
 *
 * Historically this hook carried the raw pixel values inline. Stage 3
 * Refinement extracted every layout constant into a dedicated tokens
 * module so the composition is a design system, not a collection of
 * individual offsets. This hook just returns the active tier's tokens
 * plus the raw viewport dimensions.
 */
import { useEffect, useState } from "react";
import { tokensFor } from "../lib/northstar/layoutTokens";

export function useNorthstarSizing() {
  const [vw, setVw] = useState(
    typeof window === "undefined" ? 1600 : window.innerWidth
  );
  const [vh, setVh] = useState(
    typeof window === "undefined" ? 900 : window.innerHeight
  );

  useEffect(() => {
    const compute = () => {
      setVw(window.innerWidth);
      setVh(window.innerHeight);
    };
    compute();
    window.addEventListener("resize", compute);
    return () => window.removeEventListener("resize", compute);
  }, []);

  const t = tokensFor(vw);
  // Back-compat surface: existing consumers read `identity`, `orb`,
  // `columnX`, `rowYs`, `nameSize`, `descSize`, `descMaxW`,
  // `descAtRest`, `labelStack`, `headerLayout`. Alias tokens onto
  // those names so the shell + OrbCanvas need not learn new keys.
  return {
    vw,
    vh,
    ...t,
    // aliases
    identity: t.portraitSize,
    orb: t.emblemSize,
    descMaxW: t.textMaxW,
  };
}

// Named export mirror for anywhere that used the legacy computeTier fn.
export function tierForViewport(vw) {
  return tokensFor(vw);
}
